import { Controller } from "@hotwired/stimulus"

// Toggles `.has-overflow` / `.scrolled-bottom` classes on the wrapper based on
// the current scroll state of `data-widget-scroll-target="scroll"`. The CSS
// uses those classes to show/hide a small "More ↓" chip and bottom fade.
// Native scrollbars are hidden via `.widget-scroll`.
export default class extends Controller {
  static targets = ["scroll"]

  connect() {
    if (!this.hasScrollTarget) return
    this.boundUpdate = () => this.update()
    this.update()
    this.scrollTarget.addEventListener("scroll", this.boundUpdate, { passive: true })
    window.addEventListener("resize", this.boundUpdate)
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.boundUpdate)
      this.resizeObserver.observe(this.scrollTarget)
    }
  }

  disconnect() {
    if (this.boundUpdate && this.hasScrollTarget) {
      this.scrollTarget.removeEventListener("scroll", this.boundUpdate)
    }
    if (this.boundUpdate) {
      window.removeEventListener("resize", this.boundUpdate)
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  update() {
    const el = this.scrollTarget
    // Slack: sub-pixel layout rounding can produce diffs up to ~3px, so only
    // count as overflowing when the gap is meaningful.
    const hasOverflow = (el.scrollHeight - el.clientHeight) > 4
    const atBottom = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - 4)
    this.element.classList.toggle("has-overflow", hasOverflow)
    this.element.classList.toggle("scrolled-bottom", atBottom)
  }

  // Clicking the "More" pill nudges the scroll down by ~75% of the visible
  // area. If we're already near the bottom, jump straight to the end.
  scrollMore(event) {
    event.preventDefault()
    if (!this.hasScrollTarget) return
    const el = this.scrollTarget
    const remaining = el.scrollHeight - (el.scrollTop + el.clientHeight)
    const step = Math.max(40, el.clientHeight * 0.75)
    const target = remaining <= step ? el.scrollHeight : el.scrollTop + step
    el.scrollTo({ top: target, behavior: "smooth" })
  }
}
